// src/components/AppComponents/UserTracking/UserTracking.tsx

import { useEffect, useRef } from 'react';
import Gleap from 'gleap';
import * as Sentry from '@sentry/react';

import { restartGleap } from '@/shared/lib/gleap';
import { setSentryUser } from '@/shared/lib/sentry';
import { useUserProfileStore } from '@/domains/user/userProfile.store';
import { useUserCustomersStore } from '@/domains/user/userCustomers.store';

const identifyGleapUser = Gleap.identify as (
  ...args: Parameters<typeof Gleap.identify>
) => Promise<void> | undefined;

const UserTracking = () => {
  const userProfile = useUserProfileStore((state) => state.user);
  const selectedCustomer = useUserCustomersStore(
    (state) => state.selectedCustomer,
  );
  const lastGleapUserId = useRef<string | null>(null);

  useEffect(() => {
    if (
      userProfile &&
      selectedCustomer &&
      selectedCustomer.customer_user_uuid
    ) {
      const gleapUserId = selectedCustomer.customer_user_uuid.toString();
      // Switching profile: restart Gleap so the widget reloads news etc. for
      // the new user instead of showing the previous profile's content.
      if (lastGleapUserId.current && lastGleapUserId.current !== gleapUserId) {
        restartGleap();
      }
      lastGleapUserId.current = gleapUserId;

      const fullName = `${userProfile.first_name} ${userProfile.last_name}`;
      identifyGleapUser(gleapUserId, {
        name: `${fullName} from ${selectedCustomer.name}`,
        email: userProfile.email,
        phone: userProfile.phone || '',
        company: { id: selectedCustomer.uuid, name: selectedCustomer.name },
        customData: {
          customerType: selectedCustomer.type,
        },
      })?.catch((error) => {
        console.warn(
          'Gleap identify failed; widget stays anonymous and audience-targeted content (news, tours) will not show.',
          error,
        );
        Sentry.captureMessage('Gleap identify failed', 'warning');
      });
      setSentryUser({
        id: gleapUserId,
        email: userProfile.email,
        name: fullName,
      });
    }
  }, [userProfile, selectedCustomer]);

  return null;
};

export default UserTracking;
